import { useEffect, useRef, useState } from "react";

import { useIdleTimer } from "react-idle-timer";

import { PROMPT_BEFORE_IDLE_MS, SESSION_TIMEOUT_MS } from "./session";
import SessionWarningModal from "../components/SessionWarningModal";

interface AutoLogoutProps {
  enabled: boolean;
  onIdle: () => void;
}

const IdleWatcher = ({ onIdle }: { onIdle: () => void }) => {
  const [prompted, setPrompted] = useState(false);
  const [remaining, setRemaining] = useState(PROMPT_BEFORE_IDLE_MS);
  const endingRef = useRef(false);

  const endSession = () => {
    // The timer keeps firing while sign-out is in flight, and signing out twice races the redirect.
    if (endingRef.current) return;
    endingRef.current = true;
    onIdle();
  };

  const { getRemainingTime, activate } = useIdleTimer({
    timeout: SESSION_TIMEOUT_MS,
    promptBeforeIdle: PROMPT_BEFORE_IDLE_MS,
    crossTab: true,
    syncTimers: 200,
    // The library default also counts visibilitychange and focus as activity, so
    // hiding the tab, showing it again, or alt-tabbing back each reset the clock —
    // none of which is a user doing anything. Locking the screen fires
    // visibilitychange too, which granted a fresh full window at the exact moment
    // someone walked away. Genuine input only.
    events: [
      "mousemove",
      "mousedown",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
    ],
    // A late mouse move must not revive a session the server has stopped honouring.
    stopOnIdle: true,
    onPrompt: () => setPrompted(true),
    onActive: () => setPrompted(false),
    onIdle: endSession,
  });

  useEffect(() => {
    if (!prompted) return;

    setRemaining(getRemainingTime());
    const id = window.setInterval(() => setRemaining(getRemainingTime()), 1000);
    return () => window.clearInterval(id);
  }, [prompted, getRemainingTime]);

  return <SessionWarningModal open={prompted} msRemaining={remaining} onStay={activate} />;
};

// Split out so the timer mounts only with a session: no listeners or cross-tab channels when signed out.
const AutoLogout = ({ enabled, onIdle }: AutoLogoutProps) => (enabled ? <IdleWatcher onIdle={onIdle} /> : null);

export default AutoLogout;
